<template>
  <div class="game-detail-page">
    <!-- 面包屑导航 -->
    <div class="breadcrumb-section" v-if="game">
      <div class="container mx-auto px-4 py-4">
        <nav class="flex" aria-label="Breadcrumb">
          <ol class="inline-flex items-center space-x-1 md:space-x-3">
            <li class="inline-flex items-center">
              <router-link to="/" class="inline-flex items-center text-sm font-medium text-gray-400 hover:text-white">
                <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"></path>
                </svg>
                Home
              </router-link>
            </li>
            <li>
              <div class="flex items-center">
                <svg class="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"></path>
                </svg>
                <router-link to="/games" class="ml-1 text-sm font-medium text-gray-400 hover:text-white md:ml-2">Games</router-link>
              </div>
            </li>
            <li>
              <div class="flex items-center">
                <svg class="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"></path>
                </svg>
                <span class="ml-1 text-sm font-medium text-white md:ml-2">{{ game.name }}</span>
              </div>
            </li>
          </ol>
        </nav>
      </div>
    </div>

    <!-- 游戏详情头部 -->
    <div class="game-header" v-if="game">
      <div class="container mx-auto px-4 py-8">
        <div class="flex flex-col lg:flex-row gap-8">
          <!-- 游戏图片 -->
          <div class="lg:w-1/3">
            <div class="game-image-container">
              <img
                :src="game.thumb6 || game.thumb5 || game.thumb4"
                :alt="game.name"
                class="w-full h-64 lg:h-80 object-cover rounded-lg shadow-lg"
                @error="handleImageError"
              />
            </div>
          </div>

          <!-- 游戏信息 -->
          <div class="lg:w-2/3">
            <h1 class="text-3xl lg:text-4xl font-bold text-white mb-4">{{ game.name }}</h1>
            <p class="text-gray-300 text-lg mb-6 leading-relaxed">{{ game.description }}</p>
            
            <!-- 分类和日期 -->
            <div class="mb-6">
              <div class="flex items-center gap-4 mb-3">
                <span class="px-3 py-1 bg-blue-600 text-white text-sm rounded-full">
                  {{ game.category }}
                </span>
                <span class="text-gray-400 text-sm">
                  Released: {{ formatDate(game.create_date) }}
                </span>
              </div>
            </div>

            <!-- 操作按钮 -->
            <div class="flex flex-col sm:flex-row gap-4">
              <button
                @click="startGame"
                class="bg-blue-600 hover:bg-blue-700 text-white py-3 px-8 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center"
              >
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                Start Game
              </button>
              <button
                @click="goBack"
                class="bg-gray-600 hover:bg-gray-700 text-white py-3 px-8 rounded-lg font-semibold transition-colors duration-200"
              >
                Back to List
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 游戏界面 -->
    <div v-if="isGameStarted" class="game-container">
      <div class="container mx-auto px-4 py-8">
        <!-- 游戏控制栏 -->
        <div class="game-controls bg-gray-800 rounded-lg p-4 mb-6">
          <div class="flex items-center justify-between">
            <h2 class="text-xl font-semibold text-white">{{ game?.name }}</h2>
            <div class="flex gap-2">
              <button
                @click="toggleFullscreen"
                class="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"></path>
                </svg>
              </button>
              <button
                @click="stopGame"
                class="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
              >
                Exit Game
              </button>
            </div>
          </div>
        </div>

        <!-- 游戏iframe -->
        <div class="game-frame-container bg-black rounded-lg overflow-hidden shadow-lg">
          <iframe
            v-if="game?.embed"
            :src="game.url"
            class="w-full h-96 lg:h-[600px] border-0"
            allowfullscreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          ></iframe>
          <div v-else class="w-full h-96 lg:h-[600px] flex items-center justify-center text-gray-400">
            <div class="text-center">
              <svg class="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <p>Game Loading...</p>
            </div>
          </div>
        </div>

        <!-- 游戏说明 -->
        <div class="game-instructions bg-gray-800 rounded-lg p-6 mt-6">
          <h3 class="text-xl font-semibold text-white mb-4">Game Instructions</h3>
          <div class="text-gray-300 space-y-2">
            <p>• Games will load in iframe, please ensure your browser supports iframe</p>
            <p>• Some games may require keyboard or mouse controls</p>
            <p>• If the game fails to load, please refresh the page or try again later</p>
            <p>• We recommend using a modern browser for the best gaming experience</p>
          </div>
        </div>

        <!-- 游戏详细信息 - 增加内容密度帮助自动广告找到插入点 -->
        <section class="game-details-extra bg-gray-800 rounded-lg p-6 mt-6">
          <h3 class="text-xl font-semibold text-white mb-4">About This Game</h3>
          <div class="text-gray-300 space-y-4">
            <p>{{ game?.description }}</p>
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div class="bg-gray-700 rounded-lg p-3 text-center">
                <div class="text-sm text-gray-400">Category</div>
                <div class="text-white font-medium">{{ game?.category }}</div>
              </div>
              <div class="bg-gray-700 rounded-lg p-3 text-center">
                <div class="text-sm text-gray-400">Platform</div>
                <div class="text-white font-medium">Browser</div>
              </div>
              <div class="bg-gray-700 rounded-lg p-3 text-center">
                <div class="text-sm text-gray-400">Price</div>
                <div class="text-green-400 font-medium">Free</div>
              </div>
              <div class="bg-gray-700 rounded-lg p-3 text-center">
                <div class="text-sm text-gray-400">Released</div>
                <div class="text-white font-medium">{{ formatDate(game?.create_date) }}</div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-else-if="loading" class="loading-container">
      <div class="container mx-auto px-4 py-16">
        <div class="text-center">
          <div class="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p class="text-gray-300 text-lg">Loading game information...</p>
        </div>
      </div>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-container">
      <div class="container mx-auto px-4 py-16">
        <div class="text-center">
          <svg class="w-16 h-16 mx-auto mb-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <h2 class="text-2xl font-bold text-white mb-4">Game Not Found</h2>
          <p class="text-gray-300 mb-6">Sorry, we couldn't find the game you requested.</p>
          <button
            @click="goBack"
            class="bg-blue-600 hover:bg-blue-700 text-white py-3 px-8 rounded-lg font-semibold transition-colors duration-200"
          >
            Back to Game List
          </button>
        </div>
      </div>
    </div>

    <!-- 相关游戏推荐 -->
    <div v-if="relatedGames.length > 0 && !loading" class="related-games-section">
      <div class="container mx-auto px-4 py-12">
        <h2 class="text-2xl lg:text-3xl font-bold text-white mb-8">You May Also Like</h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div
            v-for="relGame in relatedGames"
            :key="relGame.name"
            class="game-card bg-gray-800 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer hover:transform hover:scale-105"
            @click="viewRelatedGame(relGame)"
          >
            <div class="relative">
              <img
                :src="relGame.thumb6 || relGame.thumb5 || relGame.thumb4"
                :alt="relGame.name"
                class="w-full h-40 object-cover"
                @error="handleImageError"
              />
              <div class="absolute top-2 left-2">
                <span class="px-2 py-1 bg-blue-600 text-white text-xs rounded-full">{{ relGame.category }}</span>
              </div>
            </div>
            <div class="p-4">
              <h3 class="text-base font-semibold text-white mb-1 line-clamp-1">{{ relGame.name }}</h3>
              <p class="text-gray-400 text-sm line-clamp-2">{{ relGame.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import gameService from '@/services/gameService'
import analyticsService from '@/services/analyticsService'
import StructuredDataUtils from '@/utils/structuredDataUtils'

export default {
  name: 'GameDetail',
  setup() {
    // 添加结构化数据到页面头部
    const addStructuredData = (gameData) => {
      if (!gameData) return
      
      const structuredData = {
        "@context": "https://schema.org",
        "@type": "VideoGame",
        "name": gameData.name,
        "description": gameData.description,
        "image": gameData.thumb6 || gameData.thumb5 || gameData.thumb4,
        "url": `https://cybergamehub.com/game/${encodeURIComponent(gameData.name)}`,
        "genre": gameData.category,
        "datePublished": gameData.create_date,
        "publisher": {
          "@type": "Organization",
          "name": "CyberGame Hub",
          "url": "https://cybergamehub.com"
        },
        "applicationCategory": "Game",
        "operatingSystem": "Web Browser",
        "gamePlatform": "Web Browser",
        "playMode": "SinglePlayer",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock"
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.5",
          "ratingCount": "150",
          "bestRating": "5",
          "worstRating": "1"
        }
      }
      
      // 移除现有的结构化数据
      const existingScript = document.querySelector('script[data-structured="game"]')
      if (existingScript) {
        existingScript.remove()
      }
      
      // 添加新的结构化数据
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.setAttribute('data-structured', 'game')
      script.textContent = JSON.stringify(structuredData, null, 2)
      document.head.appendChild(script)

      // 添加面包屑结构化数据
      const breadcrumbData = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://cybergamehub.com/" },
          { "@type": "ListItem", "position": 2, "name": "Games", "item": "https://cybergamehub.com/games" },
          { "@type": "ListItem", "position": 3, "name": gameData.name, "item": `https://cybergamehub.com/game/${encodeURIComponent(gameData.name)}` }
        ]
      }
      const existingBreadcrumb = document.querySelector('script[data-structured="breadcrumb"]')
      if (existingBreadcrumb) existingBreadcrumb.remove()
      const breadcrumbScript = document.createElement('script')
      breadcrumbScript.type = 'application/ld+json'
      breadcrumbScript.setAttribute('data-structured', 'breadcrumb')
      breadcrumbScript.textContent = JSON.stringify(breadcrumbData)
      document.head.appendChild(breadcrumbScript)
    }
    const route = useRoute()
    const router = useRouter()
    const game = ref(null)
    const loading = ref(true)
    const error = ref(false)
    const isGameStarted = ref(false)
    const relatedGames = ref([])

    // 获取游戏ID
    const gameId = computed(() => route.params.id)

    // 格式化日期
    const formatDate = (dateString) => {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }

    // 加载游戏数据
    const loadGame = async () => {
      try {
        loading.value = true
        error.value = false
        
        // 解码游戏ID（名称）
        const decodedGameId = decodeURIComponent(gameId.value)
        
        // 从游戏数据中查找游戏
        const allGames = gameService.getAllGames()
        const foundGame = allGames.find(g => g.name === decodedGameId)
        
        if (foundGame) {
          console.log('Game found successfully:', foundGame.name)
          game.value = foundGame
          // 添加结构化数据
          addStructuredData(foundGame)
          // 动态更新页面标题和描述
          document.title = `${foundGame.name} - Play Free Online | CyberGame Hub`
          const metaDesc = document.querySelector('meta[name="description"]')
          if (metaDesc) {
            metaDesc.setAttribute('content', `Play ${foundGame.name} online for free. ${foundGame.description} Category: ${foundGame.category}. No download required.`)
          }
          // 跟踪页面浏览
          analyticsService.trackPageView(foundGame.name, `/game/${gameId.value}`)
          // 加载相关游戏
          loadRelatedGames(foundGame)
        } else {
          console.error('Game not found:', decodedGameId)
          console.log('Available games:', allGames.map(g => g.name).slice(0, 5))
          console.log('Total games available:', allGames.length)
          error.value = true
        }
      } catch (err) {
        console.error('Failed to load game:', err)
        error.value = true
      } finally {
        loading.value = false
      }
    }

    // 开始游戏
    const startGame = () => {
      isGameStarted.value = true
      // 跟踪游戏开始事件
      if (game.value) {
        analyticsService.trackGameStart(game.value.name, game.value.category)
      }
      // 滚动到游戏区域
      setTimeout(() => {
        const gameContainer = document.querySelector('.game-container')
        if (gameContainer) {
          gameContainer.scrollIntoView({ behavior: 'smooth' })
        }
      }, 100)
    }

    // 停止游戏
    const stopGame = () => {
      isGameStarted.value = false
    }

    // 切换全屏
    const toggleFullscreen = () => {
      const gameFrame = document.querySelector('.game-frame-container iframe')
      if (gameFrame) {
        if (document.fullscreenElement) {
          document.exitFullscreen()
        } else {
          gameFrame.requestFullscreen()
        }
      }
    }

    // 返回上一页
    const goBack = () => {
      router.go(-1)
    }

    // 加载相关游戏（同分类）
    const loadRelatedGames = (currentGame) => {
      try {
        const allGames = gameService.getAllGames()
        relatedGames.value = allGames
          .filter(g => g.category === currentGame.category && g.name !== currentGame.name)
          .slice(0, 4)
        // 如果同分类不够4个，补充其他游戏
        if (relatedGames.value.length < 4) {
          const others = allGames
            .filter(g => g.name !== currentGame.name && !relatedGames.value.find(r => r.name === g.name))
            .slice(0, 4 - relatedGames.value.length)
          relatedGames.value = [...relatedGames.value, ...others]
        }
      } catch (err) {
        relatedGames.value = []
      }
    }

    // 查看相关游戏
    const viewRelatedGame = (relGame) => {
      router.push(`/game/${encodeURIComponent(relGame.name)}`)
    }

    // 处理图片错误
    const handleImageError = (event) => {
      event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDMwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjMTAxMDEwIi8+Cjx0ZXh0IHg9IjE1MCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM2NjY2NjYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5HYW1lIEltYWdlPC90ZXh0Pgo8L3N2Zz4K'
    }

    onMounted(() => {
      loadGame()
    })

    return {
      game,
      loading,
      error,
      isGameStarted,
      relatedGames,
      formatDate,
      startGame,
      stopGame,
      toggleFullscreen,
      goBack,
      viewRelatedGame,
      handleImageError
    }
  }
}
</script>

<style scoped>
.game-detail-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
}

.game-header {
  background: linear-gradient(135deg, rgba(26, 26, 46, 0.9) 0%, rgba(22, 33, 62, 0.9) 100%);
  backdrop-filter: blur(10px);
}

.game-image-container {
  position: relative;
  overflow: hidden;
  border-radius: 0.5rem;
}

.game-image-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(59, 130, 246, 0.1), rgba(147, 51, 234, 0.1));
  z-index: 1;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.game-image-container:hover::before {
  opacity: 1;
}

.game-frame-container {
  position: relative;
  background: #000;
}

.game-frame-container iframe {
  display: block;
}

.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.related-games-section {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.game-card {
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.game-card:hover {
  border-color: rgba(59, 130, 246, 0.5);
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .game-frame-container iframe {
    height: 400px;
  }
}

@media (max-width: 640px) {
  .game-frame-container iframe {
    height: 300px;
  }
}

/* 加载动画 */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style> 